import React, { useState } from "react";
import {
  ChevronDown,
  ChevronUp,
  MapPin,
  Phone,
  Mail,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ScrollToTop from "../ScrollToTop";
import logo from "../../assets/logo/footerlogo.png";
import laCities from "../data/laCities";
import venturaCities from "../data/venturaCities";
import orangeCountyCities from "../data/orangeCountyCities";
import companyLinksFooter from "../data/companyLinksFooter";
import { Link } from "react-router-dom";

import CommunityLinks from "../global/CommunityLinks";

import { useParams } from "react-router-dom";
import { isLangSupported, DEFAULT_LANG } from "../../utils/langUtils";

export default function Footer() {
  const { t } = useTranslation();
  const { lang: urlLang } = useParams();
  const [showLA, setShowLA] = useState(false);
  const [showVentura, setShowVentura] = useState(false);
  const [showOrangeCounty, setShowOrangeCounty] = useState(false);

  // Use dynamic utility to check language support
  const routePrefix = isLangSupported(urlLang) ? urlLang : DEFAULT_LANG;

  return (
    <footer className="bg-[#33528b] text-white">
      <div className="container flex flex-col gap-[25px] mx-auto px-6 py-8">
        <div className="f-col flex justify-between gap-[30px]">
          <div className="w-100 w-[50%] space-y-4">
            <div className="w-80 h-24">
              <img
                src={logo}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <p className="text-sm leading-relaxed text-slate-100">
              {t(
                "Compassionate hospice care serving families with dignity and comfort across Los Angeles, Ventura, and Orange Counties.",
              )}
            </p>
            <div className="flex gap-3">
              <CommunityLinks />
            </div>
          </div>

          <div className="g-20-res f-col w-100 w-[50%] flex justify-between gap-[30px]">
            <div>
              <h4 className="font-bold text-xl mb-3">{t("Company")}</h4>
              <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                {companyLinksFooter.map((link) => (
                  <div key={link.name} className="text-sm text-slate-100">
                    {link.external ? (
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-white transition"
                      >
                        {t(link.name)}
                      </a>
                    ) : (
                      <Link
                        to={`/${routePrefix}${link.url}`}
                        className="hover:text-white transition"
                      >
                        {t(link.name)}
                      </Link>
                    )}
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-4">
              <h4 className="font-bold text-xl mb-3">{t("Contact Us")}</h4>
              <div className="space-y-3 text-sm text-slate-100">
                <div className="flex items-start gap-3">
                  <Phone size={16} className="mt-0.5" />
                  <div>
                    <p>818-791-0611</p>
                    <p>805-870-0103</p>
                    <p>805-367-4073</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail size={16} className="mt-0.5" />
                  <div className="flex flex-col space-y-2">
                    <a
                      href="mailto:info@westlakevillagehospiceinc.com"
                      className="hover:text-white inline-flex items-center min-h-[44px] py-2 px-1"
                    >
                      info@westlakevillagehospiceinc.com
                    </a>
                    <a
                      href="mailto:Westlakevillagehospice@yahoo.com"
                      className="hover:text-white inline-flex items-center min-h-[44px] py-2 px-1"
                    >
                      Westlakevillagehospice@yahoo.com
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <MapPin size={16} className="mt-0.5" />
                  <a
                    href="https://maps.app.goo.gl/sjqxaMk2U72vyVrf8"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="hover:text-white leading-relaxed"
                  >
                    {t(
                      "4165 E Thousand Oaks Blvd, Suite 325D Westlake Village, CA 91362",
                    )}
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="flex flex-col lg:flex-row space-y-4 lg:space-y-0 justify-between gap-[20px]">
          <div className="w-full lg:w-[32%] bg-white/5 rounded-xl p-4 border border-white/10 relative">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-semibold text-sm">
                {t("Los Angeles County, California")}
              </h5>
              <button
                type="button"
                onClick={() => setShowLA(!showLA)}
                aria-expanded={showLA}
                aria-controls="la-cities-list"
                className="text-xs font-medium flex items-center gap-1 hover:text-slate-100 transition text-slate-100"
              >
                {showLA ? (
                  <>
                    {" "}
                    {t("Show Less")} <ChevronUp size={14} aria-hidden="true" />
                  </>
                ) : (
                  <>
                    {" "}
                    {t("Show More")}{" "}
                    <ChevronDown size={14} aria-hidden="true" />
                  </>
                )}
              </button>
            </div>
            <div
              id="la-cities-list"
              className={`transition-[max-height] duration-300 ease-in-out ${showLA
                ? "max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                : "max-h-[100px] overflow-hidden"
                }`}
            >
              <div className="flex flex-wrap gap-2">
                {laCities.map((city) => (
                  <span
                    key={city}
                    className="text-xs bg-white/10 px-3 py-1.5 rounded-full whitespace-nowrap"
                  >
                    {city}
                  </span>
                ))}
              </div>
            </div>
            {!showLA && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[rgba(51,82,139,0.95)] to-transparent pointer-events-none rounded-b-xl" />
            )}
          </div>

          <div className="w-full lg:w-[32%] bg-white/5 rounded-xl p-4 border border-white/10 relative">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-semibold text-sm">
                {t("Ventura County, California")}
              </h5>
              <button
                type="button"
                onClick={() => setShowVentura(!showVentura)}
                aria-expanded={showVentura}
                aria-controls="ventura-cities-list"
                className="text-xs font-medium flex items-center gap-1 hover:text-slate-100 transition text-slate-100"
              >
                {showVentura ? (
                  <>
                    {" "}
                    {t("Show Less")} <ChevronUp size={14} aria-hidden="true" />
                  </>
                ) : (
                  <>
                    {" "}
                    {t("Show More")}{" "}
                    <ChevronDown size={14} aria-hidden="true" />
                  </>
                )}
              </button>
            </div>
            <div
              id="ventura-cities-list"
              className={`transition-[max-height] duration-300 ease-in-out ${showVentura
                ? "max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                : "max-h-[100px] overflow-hidden"
                }`}
            >
              <div className="flex flex-wrap gap-2">
                {venturaCities.map((city) => (
                  <span
                    key={city}
                    className="text-xs bg-white/10 px-3 py-1.5 rounded-full whitespace-nowrap"
                  >
                    {city}
                  </span>
                ))}
              </div>
            </div>
            {!showVentura && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[rgba(51,82,139,0.95)] to-transparent pointer-events-none rounded-b-xl" />
            )}
          </div>

          <div className="w-full lg:w-[32%] bg-white/5 rounded-xl p-4 border border-white/10 relative">
            <div className="flex justify-between items-center mb-2">
              <h5 className="font-semibold text-sm">
                {t("Orange County, California")}
              </h5>
              <button
                type="button"
                onClick={() => setShowOrangeCounty(!showOrangeCounty)}
                aria-expanded={showOrangeCounty}
                aria-controls="orange-county-cities-list"
                className="text-xs font-medium flex items-center gap-1 hover:text-slate-100 transition text-slate-100"
              >
                {showOrangeCounty ? (
                  <>
                    {" "}
                    {t("Show Less")} <ChevronUp size={14} aria-hidden="true" />
                  </>
                ) : (
                  <>
                    {" "}
                    {t("Show More")}{" "}
                    <ChevronDown size={14} aria-hidden="true" />
                  </>
                )}
              </button>
            </div>
            <div
              id="orange-county-cities-list"
              className={`transition-[max-height] duration-300 ease-in-out ${showOrangeCounty
                ? "max-h-[200px] overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]"
                : "max-h-[100px] overflow-hidden"
                }`}
            >
              <div className="flex flex-wrap gap-2">
                {orangeCountyCities.map((city) => (
                  <span
                    key={city}
                    className="text-xs bg-white/10 px-3 py-1.5 rounded-full whitespace-nowrap"
                  >
                    {city}
                  </span>
                ))}
              </div>
            </div>
            {!showOrangeCounty && (
              <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-[rgba(51,82,139,0.95)] to-transparent pointer-events-none rounded-b-xl" />
            )}
          </div>
        </div>

        <div className="border-t border-white/20 pt-4 text-center text-xs text-slate-100 space-y-2">
          {/* Disclaimer text */}
          <p className="text-xs text-slate-300 leading-tight max-w-4xl mx-auto mb-2 opacity-80">
            {t(
              "Disclaimer: Westlake Village Hospice Inc. is a licensed and Medicare-certified hospice care provider. We do not sell, prescribe, dispense, or ship prescription medications, and we do not operate an online pharmacy or telemedicine prescribing service. All hospice services are provided in person by licensed healthcare professionals. Any references to Medicare coverage, medication support, or medical coordination relate solely to patient care delivered under physician direction and do not constitute prescription drug sales or online medical services.",
            )}
          </p>

          <p>
            {t("© 2026 Westlake Village Hospice Inc. All rights reserved.")}
          </p>
          <div className="flex items-center justify-center gap-4 flex-wrap">
            <span>design by :</span>
            <a
              href="https://saasbizhub.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-blue-200 transition-colors underline"
            >
              saasbizhub
            </a>
            <span className="text-slate-200">|</span>
            <span>developed by :</span>
            <a
              href="https://saasbizhub.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-white hover:text-blue-200 transition-colors underline"
            >
              saasbizhub
            </a>
          </div>
        </div>
      </div>
      <ScrollToTop />
    </footer>
  );
}
